import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

// ---------- helpers ----------
async function ensureAdmin(userId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (error) throw new Error("role check failed");
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin") && !roles.includes("owner")) {
    throw new Error("forbidden");
  }
  return { isOwner: roles.includes("owner") };
}

// ---------- am I admin / what's my role ----------
export const getMyRole = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    const roles = (data ?? []).map((r: { role: string }) => r.role);
    return {
      isAdmin: roles.includes("admin") || roles.includes("owner"),
      isOwner: roles.includes("owner"),
      isConciergeAgent: roles.includes("concierge_agent"),
    };
  });


// ---------- premium status for current user (includes manual grants) ----------
export const getMyPremiumDetails = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [roles, grants] = await Promise.all([
      supabaseAdmin.from("user_roles").select("role").eq("user_id", context.userId),
      supabaseAdmin
        .from("manual_premium_grants")
        .select("expires_at, reason, revoked_at")
        .eq("user_id", context.userId)
        .is("revoked_at", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);
    const roleNames = (roles.data ?? []).map((r: { role: string }) => r.role);
    const isAdmin = roleNames.includes("admin") || roleNames.includes("owner");
    const grant = grants.data as { expires_at: string | null; reason: string | null; revoked_at: string | null } | null;
    const hasManualGrant = !!grant && (!grant.expires_at || new Date(grant.expires_at) > new Date());
    return {
      isAdmin,
      hasManualGrant,
      manualGrant: hasManualGrant ? grant : null,
    };
  });

/** Admin-only explanation of every source considered for a user's effective tier. */
export const getUserEntitlementDiagnostics = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { resolveUserPlanInternal } = await import("@/lib/quota.server");
    const [resolved, roles, subscriptions, grants] = await Promise.all([
      resolveUserPlanInternal(supabaseAdmin, data.userId),
      supabaseAdmin.from("user_roles").select("role,created_at").eq("user_id", data.userId),
      supabaseAdmin
        .from("subscriptions")
        .select("price_id,status,current_period_end,cancel_at_period_end,cancel_at,environment,updated_at")
        .eq("user_id", data.userId)
        .order("updated_at", { ascending: false }),
      supabaseAdmin
        .from("manual_premium_grants")
        .select("expires_at,revoked_at,reason,created_at")
        .eq("user_id", data.userId)
        .order("created_at", { ascending: false }),
    ]);
    return {
      checkedAt: new Date().toISOString(),
      plan: resolved.plan,
      entitlement: resolved.entitlement,
      roles: roles.data ?? [],
      subscriptions: subscriptions.data ?? [],
      grants: grants.data ?? [],
      errors: [roles.error, subscriptions.error, grants.error]
        .filter(Boolean)
        .map((error) => error?.message ?? "unknown"),
    };
  });

// ---------- list users (admin only) ----------
export const listUsers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { search?: string; limit?: number; inactiveDays?: number | null }) =>
    z
      .object({
        search: z.string().max(120).optional(),
        limit: z.number().min(1).max(200).default(50),
        inactiveDays: z.number().int().min(1).max(3650).nullable().optional(),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // List up to N users from auth.users via admin API
    const { data: usersPage, error } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: data.limit });
    if (error) throw new Error(error.message);
    let users = usersPage.users;
    if (data.search?.trim()) {
      const q = data.search.trim().toLowerCase();
      users = users.filter((u) => (u.email ?? "").toLowerCase().includes(q));
    }
    if (data.inactiveDays) {
      const cutoff = Date.now() - data.inactiveDays * 86400_000;
      users = users.filter((u) => {
        const last = u.last_sign_in_at ? new Date(u.last_sign_in_at).getTime() : new Date(u.created_at).getTime();
        return last < cutoff;
      });
    }

    const ids = users.map((u) => u.id);
    if (ids.length === 0) return { users: [] };

    const [profiles, subs, grants, roles, routesCount, generatedCount] = await Promise.all([
      supabaseAdmin.from("profiles").select("id, display_name").in("id", ids),
      supabaseAdmin
        .from("subscriptions")
        .select("user_id, status, price_id, current_period_end, cancel_at_period_end, environment")
        .in("user_id", ids)
        .order("created_at", { ascending: false }),
      supabaseAdmin
        .from("manual_premium_grants")
        .select("user_id, expires_at, reason, revoked_at, created_at")
        .in("user_id", ids)
        .is("revoked_at", null),
      supabaseAdmin.from("user_roles").select("user_id, role").in("user_id", ids),
      supabaseAdmin.from("saved_itineraries").select("user_id").in("user_id", ids),
      supabaseAdmin.from("usage_counters").select("user_id").eq("kind", "itinerary").in("user_id", ids),
    ]);


    const profileMap = new Map<string, string | null>();
    (profiles.data ?? []).forEach((p: { id: string; display_name: string | null }) => profileMap.set(p.id, p.display_name));

    const subMap = new Map<string, any>();
    (subs.data ?? []).forEach((s: any) => {
      if (!subMap.has(s.user_id)) subMap.set(s.user_id, s);
    });

    const grantMap = new Map<string, any>();
    (grants.data ?? []).forEach((g: any) => {
      if (g.expires_at && new Date(g.expires_at) <= new Date()) return;
      grantMap.set(g.user_id, g);
    });

    const rolesMap = new Map<string, string[]>();
    (roles.data ?? []).forEach((r: { user_id: string; role: string }) => {
      const arr = rolesMap.get(r.user_id) ?? [];
      arr.push(r.role);
      rolesMap.set(r.user_id, arr);
    });

    const routesMap = new Map<string, number>();
    (routesCount.data ?? []).forEach((r: { user_id: string }) => {
      routesMap.set(r.user_id, (routesMap.get(r.user_id) ?? 0) + 1);
    });

    const generatedMap = new Map<string, number>();
    (generatedCount.data ?? []).forEach((r: { user_id: string | null }) => {
      if (!r.user_id) return;
      generatedMap.set(r.user_id, (generatedMap.get(r.user_id) ?? 0) + 1);
    });



    return {
      users: users.map((u) => {
        const sub = subMap.get(u.id);
        const grant = grantMap.get(u.id);
        const userRoles = rolesMap.get(u.id) ?? [];
        const isAdmin = userRoles.includes("admin") || userRoles.includes("owner");
        const subActive =
          !!sub &&
          (["active", "trialing", "past_due"].includes(sub.status) &&
            (!sub.current_period_end || new Date(sub.current_period_end) > new Date()));
        return {
          id: u.id,
          email: u.email ?? "",
          displayName: profileMap.get(u.id) ?? null,
          createdAt: u.created_at,
          lastSignInAt: u.last_sign_in_at,
          bannedUntil: (u as { banned_until?: string | null }).banned_until ?? null,
          isAdmin,
          isOwner: userRoles.includes("owner"),
          isPro: userRoles.includes("pro"),
          isConciergeAgent: userRoles.includes("concierge_agent"),
          isPremium: isAdmin || !!grant || subActive,

          subscription: sub
            ? {
                status: sub.status,
                priceId: sub.price_id,
                currentPeriodEnd: sub.current_period_end,
                cancelAtPeriodEnd: sub.cancel_at_period_end,
                environment: sub.environment,
              }
            : null,
          manualGrant: grant
            ? { expiresAt: grant.expires_at, reason: grant.reason, createdAt: grant.created_at }
            : null,
          routesCount: routesMap.get(u.id) ?? 0,
          generatedCount: generatedMap.get(u.id) ?? 0,

        };
      }),
    };
  });

// ---------- grant / revoke manual premium ----------
export const grantPremium = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string; days?: number | null; reason?: string }) =>
    z
      .object({
        userId: z.string().uuid(),
        days: z.number().int().min(1).max(36500).nullable().optional(),
        reason: z.string().max(280).optional(),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const expiresAt = data.days ? new Date(Date.now() + data.days * 86400_000).toISOString() : null;
    const { error } = await supabaseAdmin.from("manual_premium_grants").insert({
      user_id: data.userId,
      granted_by: context.userId,
      expires_at: expiresAt,
      reason: data.reason ?? null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const revokePremium = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("manual_premium_grants")
      .update({ revoked_at: new Date().toISOString() })
      .eq("user_id", data.userId)
      .is("revoked_at", null);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- grant / revoke Pro role (Routify Pro) ----------
export const grantPro = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("user_roles")
      .insert({ user_id: data.userId, role: "pro" });
    if (error && !String(error.message).toLowerCase().includes("duplicate")) {
      throw new Error(error.message);
    }
    return { ok: true };
  });

export const revokePro = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("user_roles")
      .delete()
      .eq("user_id", data.userId)
      .eq("role", "pro");
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- modo de simulação do Fundador (Free / Premium / Pro) ----------
/**
 * `owner`/`admin` dá apenas permissões administrativas — o plano comercial
 * efetivo continua a ser Free sem subscrição ou concessão. Este modo permite ao
 * próprio Fundador simular Free/Premium/Pro para validar paywalls.
 *
 * Segurança: só a própria conta Owner/Admin pode ligar/desligar, a chave é
 * `user_id + ambiente`, nunca se aplica a clientes e fica auditada.
 */
export const setFounderPreviewTier = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string; tier: "free" | "premium" | "pro" | null }) =>
    z.object({ userId: z.string().uuid(), tier: z.enum(["free", "premium", "pro"]).nullable() }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    if (data.userId !== context.userId) {
      throw new Error("O modo de simulação só pode ser alterado na própria conta.");
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { founderPreviewKey, currentFounderPreviewEnvironment } = await import("@/lib/founder-preview");
    const environment = currentFounderPreviewEnvironment();
    const key = founderPreviewKey(data.userId, environment);
    const { data: row } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", "founder_preview_tier")
      .maybeSingle();
    const current = { ...((row?.value ?? {}) as Record<string, string>) };
    const previous = current[key] ?? null;
    if (data.tier) current[key] = data.tier;
    else delete current[key];
    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert({ key: "founder_preview_tier", value: current, updated_by: context.userId }, { onConflict: "key" });
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("ops_action_log").insert({
      action: data.tier ? "founder_preview_on" : "founder_preview_off",
      actor_user_id: context.userId,
      target_kind: "user",
      target_id: data.userId,
      affected_count: 1,
      reason: "Modo de simulação do Fundador",
      idempotency_key: `founder-preview:${key}:${data.tier ?? "off"}:${Date.now()}`,
      metadata: { environment, from: previous, to: data.tier },
    });
    return { ok: true, tier: data.tier, environment };
  });

export const getFounderPreviewTier = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { founderPreviewKey, currentFounderPreviewEnvironment } = await import("@/lib/founder-preview");
    const environment = currentFounderPreviewEnvironment();
    const { data: row } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", "founder_preview_tier")
      .maybeSingle();
    const map = (row?.value ?? {}) as Record<string, string>;
    const tier = map[founderPreviewKey(data.userId, environment)];
    return { tier: tier === "free" || tier === "premium" || tier === "pro" ? tier : null, environment };
  });


// ---------- grant itinerary credits (parcerias) ----------
export const grantCredits = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string; amount: number; reason?: string }) =>
    z
      .object({
        userId: z.string().uuid(),
        amount: z.number().int().min(1).max(10000),
        reason: z.string().max(280).optional(),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.rpc("add_credits", {
      _user_id: data.userId,
      _amount: data.amount,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- team / admin management ----------
export const listAdmins = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: roles } = await supabaseAdmin.from("user_roles").select("user_id, role, created_at");
    const ids = Array.from(new Set((roles ?? []).map((r: { user_id: string }) => r.user_id)));
    if (!ids.length) return { admins: [] };
    const { data: usersPage } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 200 });
    const emailMap = new Map<string, string>();
    usersPage.users.forEach((u) => emailMap.set(u.id, u.email ?? ""));
    const merged = new Map<string, { id: string; email: string; roles: string[] }>();
    (roles ?? []).forEach((r: { user_id: string; role: string }) => {
      const entry = merged.get(r.user_id) ?? { id: r.user_id, email: emailMap.get(r.user_id) ?? "", roles: [] };
      entry.roles.push(r.role);
      merged.set(r.user_id, entry);
    });
    return { admins: Array.from(merged.values()) };
  });

export const addAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { email: string }) =>
    z.object({ email: z.string().email().max(255) }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Find user by email (paged scan)
    let page = 1;
    let foundId: string | null = null;
    const needle = data.email.trim().toLowerCase();
    while (page <= 5 && !foundId) {
      const { data: usersPage, error } = await supabaseAdmin.auth.admin.listUsers({ page, perPage: 200 });
      if (error) throw new Error(error.message);
      foundId = usersPage.users.find((u) => (u.email ?? "").toLowerCase() === needle)?.id ?? null;
      if (usersPage.users.length < 200) break;
      page++;
    }
    if (!foundId) return { ok: false, error: "Utilizador não encontrado. Tem de ter criado conta primeiro." };
    const { error } = await supabaseAdmin
      .from("user_roles")
      .insert({ user_id: foundId, role: "admin" });
    if (error && !String(error.message).includes("duplicate")) throw new Error(error.message);
    return { ok: true };
  });

export const removeAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Never allow removing an owner via the panel
    const { data: roles } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", data.userId);
    const isOwner = (roles ?? []).some((r: { role: string }) => r.role === "owner");
    if (isOwner) throw new Error("Não podes remover um owner.");
    const { error } = await supabaseAdmin
      .from("user_roles")
      .delete()
      .eq("user_id", data.userId)
      .eq("role", "admin");
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- stats ----------
export const getStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = new Date();
    const last7 = new Date(now.getTime() - 7 * 86400_000).toISOString();
    const last30 = new Date(now.getTime() - 30 * 86400_000).toISOString();
    const activeNowSince = new Date(now.getTime() - 5 * 60_000).toISOString();
    const activeTodaySince = new Date(now.getTime() - 24 * 3600_000).toISOString();
    const active7dSince = new Date(now.getTime() - 7 * 86400_000).toISOString();

    const [usersPage, subs, itin7, itin30, itinAll, activeNow, activeToday, active7d] = await Promise.all([
      supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 1 }),
      supabaseAdmin.from("subscriptions").select("status, current_period_end, price_id, environment"),
      supabaseAdmin.from("saved_itineraries").select("id", { count: "exact", head: true }).gte("created_at", last7),
      supabaseAdmin.from("saved_itineraries").select("id", { count: "exact", head: true }).gte("created_at", last30),
      supabaseAdmin.from("saved_itineraries").select("city, country"),
      supabaseAdmin.from("profiles").select("id", { count: "exact", head: true }).gte("last_seen_at", activeNowSince),
      supabaseAdmin.from("profiles").select("id", { count: "exact", head: true }).gte("last_seen_at", activeTodaySince),
      supabaseAdmin.from("profiles").select("id", { count: "exact", head: true }).gte("last_seen_at", active7dSince),
    ]);

    const subsData = (subs.data ?? []) as Array<{ status: string; current_period_end: string | null; price_id: string; environment: string }>;
    const activeLive = subsData.filter(
      (s) =>
        s.environment === "live" &&
        ((["active", "trialing", "past_due"].includes(s.status) && (!s.current_period_end || new Date(s.current_period_end) > now)) ||
          (s.status === "canceled" && s.current_period_end && new Date(s.current_period_end) > now)),
    );

    const monthlyRevenue = activeLive.reduce((sum, s) => {
      if (s.price_id === "premium_monthly") return sum + 3.99;
      if (s.price_id === "premium_yearly") return sum + 29.99 / 12;
      return sum;
    }, 0);

    // Top cities
    const cityCounts = new Map<string, number>();
    (itinAll.data ?? []).forEach((r: { city: string; country: string | null }) => {
      const key = `${r.city}${r.country ? ", " + r.country : ""}`;
      cityCounts.set(key, (cityCounts.get(key) ?? 0) + 1);
    });
    const topCities = Array.from(cityCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([city, count]) => ({ city, count }));

    return {
      totalUsers: (usersPage.data as { total?: number }).total ?? usersPage.data.users.length ?? 0,
      totalRoutes: (itinAll.data ?? []).length,
      activeLiveSubscriptions: activeLive.length,
      monthlyRevenueEUR: Math.round(monthlyRevenue * 100) / 100,
      routesLast7Days: itin7.count ?? 0,
      routesLast30Days: itin30.count ?? 0,
      activeNow: activeNow.count ?? 0,
      activeToday: activeToday.count ?? 0,
      activeLast7Days: active7d.count ?? 0,
      topCities,
    };
  });

// ---------- delete user (admin only) ----------
export const deleteUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    if (data.userId === context.userId) throw new Error("Não te podes apagar a ti próprio.");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Block deleting owners
    const { data: roles } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", data.userId);
    if ((roles ?? []).some((r: { role: string }) => r.role === "owner")) {
      throw new Error("Não podes apagar um owner.");
    }
    const { error } = await supabaseAdmin.auth.admin.deleteUser(data.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- ban / unban user (admin only) ----------
export const setUserBan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { userId: string; banned: boolean }) =>
    z.object({ userId: z.string().uuid(), banned: z.boolean() }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    if (data.userId === context.userId) throw new Error("Não te podes suspender a ti próprio.");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: roles } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", data.userId);
    if ((roles ?? []).some((r: { role: string }) => r.role === "owner")) {
      throw new Error("Não podes suspender um owner.");
    }
    const { error } = await supabaseAdmin.auth.admin.updateUserById(data.userId, {
      ban_duration: data.banned ? "876000h" : "none",
    } as { ban_duration: string });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- top places (restaurants / hotels / attractions) ----------
const RESTAURANT_RX = /(restaurant|restaurante|bistro|pizzaria|pizzeria|churrasca|tasca|taberna|brasserie|steakhouse|grill|trattoria|osteria|cantina|marisqueira|cervejaria|lounge|caf[eé]|coffee|bar\b)/i;
const HOTEL_RX = /(hotel|hostel|pousada|guesthouse|guest house|\binn\b|resort|b&b|alojamento|apartamento|aparthotel|motel|lodge|residenc)/i;

function classifyStop(name: string, category: string): "restaurant" | "hotel" | "attraction" {
  const hay = `${name} ${category}`;
  if (HOTEL_RX.test(hay)) return "hotel";
  if (RESTAURANT_RX.test(hay)) return "restaurant";
  return "attraction";
}

const TopPlacesInput = z.object({
  city: z.string().max(120).optional(),
  country: z.string().max(120).optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  limit: z.number().int().min(1).max(200).default(25),
});

export const getTopPlaces = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => TopPlacesInput.parse(d ?? {}))
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("saved_itineraries")
      .select("user_id, city, country, stops, created_at");
    if (data.city?.trim()) q = q.ilike("city", `%${data.city.trim()}%`);
    if (data.country?.trim()) q = q.ilike("country", `%${data.country.trim()}%`);
    if (data.fromDate) q = q.gte("created_at", new Date(data.fromDate).toISOString());
    if (data.toDate) {
      const end = new Date(data.toDate);
      end.setHours(23, 59, 59, 999);
      q = q.lte("created_at", end.toISOString());
    }
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    type Agg = { name: string; city: string; country: string | null; count: number; users: Set<string> };
    const buckets: Record<"restaurant" | "hotel" | "attraction", Map<string, Agg>> = {
      restaurant: new Map(),
      hotel: new Map(),
      attraction: new Map(),
    };

    (rows ?? []).forEach((row: { user_id: string; city: string; country: string | null; stops: unknown }) => {
      const stops = Array.isArray(row.stops) ? (row.stops as Array<{ name?: string; category?: string }>) : [];
      stops.forEach((s) => {
        const name = (s?.name ?? "").trim();
        if (!name) return;
        const kind = classifyStop(name, s?.category ?? "");
        const key = `${name.toLowerCase()}|${row.city.toLowerCase()}`;
        const bucket = buckets[kind];
        const cur = bucket.get(key);
        if (cur) {
          cur.count += 1;
          cur.users.add(row.user_id);
        } else {
          bucket.set(key, { name, city: row.city, country: row.country, count: 1, users: new Set([row.user_id]) });
        }
      });
    });

    const top = (m: Map<string, Agg>) =>
      Array.from(m.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, data.limit)
        .map((x) => ({ name: x.name, city: x.city, country: x.country, count: x.count, uniqueUsers: x.users.size }));

    return {
      restaurants: top(buckets.restaurant),
      hotels: top(buckets.hotel),
      attractions: top(buckets.attraction),
    };
  });

// ---------- detail for a single place ----------
const PlaceDetailInput = z.object({
  name: z.string().min(1).max(200),
  city: z.string().max(120).optional(),
  country: z.string().max(120).optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
});

export const getPlaceDetail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => PlaceDetailInput.parse(d))
  .handler(async ({ context, data }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("saved_itineraries")
      .select("user_id, city, country, stops, created_at");
    if (data.city?.trim()) q = q.ilike("city", `%${data.city.trim()}%`);
    if (data.country?.trim()) q = q.ilike("country", `%${data.country.trim()}%`);
    if (data.fromDate) q = q.gte("created_at", new Date(data.fromDate).toISOString());
    if (data.toDate) {
      const end = new Date(data.toDate);
      end.setHours(23, 59, 59, 999);
      q = q.lte("created_at", end.toISOString());
    }
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    const needle = data.name.trim().toLowerCase();
    type Occurrence = {
      city: string;
      country: string | null;
      createdAt: string;
      userId: string;
      lat?: number;
      lng?: number;
      category?: string;
    };
    const occurrences: Occurrence[] = [];
    let kind: "restaurant" | "hotel" | "attraction" = "attraction";

    (rows ?? []).forEach((row: { user_id: string; city: string; country: string | null; stops: unknown; created_at: string }) => {
      const stops = Array.isArray(row.stops) ? (row.stops as Array<{ name?: string; category?: string; lat?: number; lng?: number }>) : [];
      stops.forEach((s) => {
        if ((s?.name ?? "").trim().toLowerCase() !== needle) return;
        kind = classifyStop(s?.name ?? "", s?.category ?? "");
        occurrences.push({
          city: row.city,
          country: row.country,
          createdAt: row.created_at,
          userId: row.user_id,
          lat: typeof s.lat === "number" ? s.lat : undefined,
          lng: typeof s.lng === "number" ? s.lng : undefined,
          category: s.category,
        });
      });
    });

    // Aggregate by city for the breakdown chart
    const byCityMap = new Map<string, { city: string; country: string | null; count: number; users: Set<string> }>();
    occurrences.forEach((o) => {
      const k = `${o.city.toLowerCase()}|${(o.country ?? "").toLowerCase()}`;
      const cur = byCityMap.get(k);
      if (cur) {
        cur.count += 1;
        cur.users.add(o.userId);
      } else {
        byCityMap.set(k, { city: o.city, country: o.country, count: 1, users: new Set([o.userId]) });
      }
    });
    const byCity = Array.from(byCityMap.values())
      .map((c) => ({ city: c.city, country: c.country, count: c.count, uniqueUsers: c.users.size }))
      .sort((a, b) => b.count - a.count);

    const uniqueUsers = new Set(occurrences.map((o) => o.userId)).size;
    const firstSeen = occurrences.reduce<string | null>(
      (min, o) => (!min || o.createdAt < min ? o.createdAt : min),
      null,
    );
    const lastSeen = occurrences.reduce<string | null>(
      (max, o) => (!max || o.createdAt > max ? o.createdAt : max),
      null,
    );

    // Pick a representative coordinate (first occurrence with lat/lng)
    const sample = occurrences.find((o) => typeof o.lat === "number" && typeof o.lng === "number");

    return {
      name: data.name,
      kind,
      totalCount: occurrences.length,
      uniqueUsers,
      firstSeen,
      lastSeen,
      byCity,
      sampleCoords: sample ? { lat: sample.lat!, lng: sample.lng! } : null,
      occurrences: occurrences
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
        .slice(0, 200)
        .map((o) => ({ city: o.city, country: o.country, createdAt: o.createdAt, lat: o.lat, lng: o.lng })),
    };
  });



// ---------- Meal defaults (admin-managed, public read) ----------
export type MealDefaults = {
  lunchStart: string;
  lunchEnd: string;
  dinnerStart: string;
  dinnerEnd: string;
  eatMinutes: number;
};

const MEAL_FALLBACK: MealDefaults = {
  lunchStart: "12:00",
  lunchEnd: "14:00",
  dinnerStart: "19:00",
  dinnerEnd: "21:00",
  eatMinutes: 90,
};

export const getMealDefaults = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("app_settings")
    .select("value")
    .eq("key", "meal_defaults")
    .maybeSingle();
  const v = (data?.value ?? {}) as Partial<MealDefaults>;
  return { ...MEAL_FALLBACK, ...v } as MealDefaults;
});

const MealSchema = z.object({
  lunchStart: z.string().regex(/^\d{2}:\d{2}$/),
  lunchEnd: z.string().regex(/^\d{2}:\d{2}$/),
  dinnerStart: z.string().regex(/^\d{2}:\d{2}$/),
  dinnerEnd: z.string().regex(/^\d{2}:\d{2}$/),
  eatMinutes: z.number().int().min(15).max(240),
});

export const setMealDefaults = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => MealSchema.parse(input))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert({ key: "meal_defaults", value: data, updated_by: context.userId, updated_at: new Date().toISOString() });
    if (error) throw new Error(error.message);
    return { ok: true, value: data };
  });
